import {Component, Input, OnInit} from '@angular/core';
import {ComponentEvent, Languages, StateAwareComponent} from 'src/app/model/rest/base';
import {forkJoin, Observable, of} from 'rxjs';
import {exhaustMap, map, switchMap} from 'rxjs/operators';
import {Utils} from "../../services/utils/utils";
import {LangProductMapper} from "../../services/utils/lang-mappers";
import {ManagerColV3RestService} from "../../services/rest/v3/manager-col-v3-rest.service";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {TeacherV3RestService} from "../../services/rest/v3/teacher-rest.service";
import {V3CourseTeacherProfile, V3CourseTeacherProfileDetails, V3TeacherProfile} from "../../model/v3/schools";
import * as LessonsV3 from '../../model/v3/lessons';

export class TeacherProfileFormComponentEvent extends ComponentEvent {
  static ET_FORM_EDITABLE = 'underEdit';
  static ET_FORM_READ_ONLY = 'readOnly';
}

export class FormBack {
  skype: string;
  language: string;
  productCompetences = {};
}

export class ProductCompetencesBack {
  competences = {};
  description: string;
  accent: string;
  videoUrl: string;
}

@Component({
    selector: 'app-teacher-profile-form',
    templateUrl: './teacher-profile-form.component.html',
    styleUrls: ['./teacher-profile-form.component.css'],
    standalone: false
})
export class TeacherProfileFormComponent extends StateAwareComponent<TeacherProfileFormComponentEvent> implements OnInit {
  _editable = false;
  _teacherId: number;
  languages = Languages.values;
  formBack: FormBack = null;
  saving = false;
  _schoolId: number;
  _personId: number | null = null;
  _colSettings: LessonsV3.PersonalSettings | null = null;

  constructor(
    private managerV3Rest: ManagerColV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    private teacherV3Rest: TeacherV3RestService
  ) {
    super();
   }

  @Input()
  set editable(editable: boolean) {
    this._editable = editable;
  }

  @Input()
  set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.loadTeacherForm();
  }

  @Input()
  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
    this.loadTeacherForm();
  }

  get editable(): boolean {
    return this._editable;
  }

  get compEditable(): boolean {
    return this.editable;
  }

  showLang(_: string) {
    return true;
  }

  private saveCourseProfile(code: string, courseProfile: V3CourseTeacherProfileDetails) {
    const courseEnumCode = LangProductMapper.mapLangCodeToCourseEnumCode(code);
    return this.teacherV3Rest.updateTeacherCourseProfile(this._schoolId, this._teacherId, courseEnumCode, courseProfile);
  }

  private saveProfile(profile: V3TeacherProfile) {
    return this.teacherV3Rest.updateTeacherProfile(this._schoolId, this._teacherId, profile);
  }

  public onSave() {
    this.saving = true;
    const teacherProfile: V3TeacherProfile = {language: this.formBack.language};
    const productCompetences = this.formBack.productCompetences as { [code: string]: ProductCompetencesBack };
    const courseProfileSaves: Observable<void>[] = Object.keys(productCompetences).map((code) => {
      const langForm = productCompetences[code];
      const competences = Object.keys(langForm.competences)
        .filter((competenceCode) => (langForm.competences as { [c: string]: boolean })[competenceCode])
        .map((competenceCode) => ({code: competenceCode}));
      const courseProfile: V3CourseTeacherProfileDetails = {
        competences,
        description: langForm.description,
        accent: langForm.accent,
        videoUrl: langForm.videoUrl,
      };
      return this.saveCourseProfile(code, courseProfile);
    });
    const saves: Observable<any>[] = courseProfileSaves.length > 0 ? [...courseProfileSaves] : [of(null)];
    if (this._personId && this._colSettings) {
      saves.push(this.peopleV3Rest.updatePersonColSettings(
        this._personId,
        {...this._colSettings, skype: this.formBack.skype}
      ));
    }

    this.saveProfile(teacherProfile)
    .pipe(
      exhaustMap(() => forkJoin(saves)),
      switchMap(() => this.getProfile()),
      map ( loaded => Utils.jsonClone(loaded))
    )
    .subscribe(
      {
        next: (loaded: FormBack) => {
          this.formBack = loaded;
          this.editable = false;
        },
        complete: () => this.saving = false
      });
  }

  public activateLang(lang: string) {
    this.formBack.productCompetences[lang] = new ProductCompetencesBack();
  }

  loadTeacherForm(): any {
    if (!this._teacherId || !this._schoolId) {return; }

    this.getProfile().subscribe(
      formBack => {
        this.formBack = formBack;
        this.stateEvent.emit(ComponentEvent.initialized());
      }
    );
  }

  private getProfile() {
    return this.managerV3Rest.getTeacher(this._schoolId, this._teacherId).pipe(
      switchMap((teacher) =>
        forkJoin({
          personId: of(teacher.person.id),
          profile: this.managerV3Rest.getTeacherProfile(this._schoolId, this._teacherId),
          courseProfiles: this.teacherV3Rest.getTeacherCourseProfiles(this._schoolId, this._teacherId),
          colSettings: this.peopleV3Rest.getPersonColSettings(teacher.person.id),
        })
      ),
      map(({personId, profile, courseProfiles, colSettings}) => {
        this._personId = personId;
        this._colSettings = colSettings;
        return this.mapV3ToFormBack(profile, courseProfiles, colSettings);
      })
    );
  }

  mapV3ToFormBack(
    profile: V3TeacherProfile | null,
    courseProfiles: V3CourseTeacherProfile[] | null,
    colSettings: LessonsV3.PersonalSettings | null
  ): FormBack {
    const formBack = new FormBack();
    formBack.language = profile?.language ?? Languages.English.code;
    formBack.skype = colSettings?.skype;
    if (courseProfiles) {
      courseProfiles.forEach((productCompetence) => {
        const newProductCompetences = new ProductCompetencesBack();
        formBack.productCompetences[LangProductMapper.mapCourseEnumCodeToLangCode(productCompetence.course.code)] = newProductCompetences;
        productCompetence.competences
          .map( competence => competence.code)
          .forEach( competenceCode => newProductCompetences.competences[competenceCode] = true);
        newProductCompetences.accent = productCompetence.accent;
        newProductCompetences.description = productCompetence.description;
        newProductCompetences.videoUrl = productCompetence.videoUrl;
      });
    }

    return formBack;
  }

  ngOnInit() {
  }
}
